import {GitHubAvatar} from '@github-ui/github-avatar'
import {XIcon} from '@primer/octicons-react'
import {Heading, IconButton} from '@primer/react'

import {anchorComment} from '../../helpers/anchor-helpers'
import type {AddCommentEditorProps, CommentLineChangeConfig} from './AddCommentEditor'
import {AddCommentEditor} from './AddCommentEditor'
import {ChangeCommentLineNumberButton} from './ChangeCommentLineNumberButton'
import {useState} from 'react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

export interface StartThreadCommentProps
  extends Pick<
    AddCommentEditorProps,
    | 'batchingEnabled'
    | 'fileLevelComment'
    | 'filePath'
    | 'focusOnMount'
    | 'lineNumber'
    | 'startLineNumber'
    | 'subjectId'
    | 'suggestedChangesConfig'
  > {
  addCommentDialogTitle?: string
  isLeftSide: boolean | undefined
  onAddComment: (args: {
    onCompleted?: (threadId: string, commentDatabaseId: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean | undefined
    text: string
  }) => void
  onClose?: (shouldFocusCell?: boolean) => void
  viewerData: {
    avatarUrl: string
    login: string
  }
  showOnCloseIcon?: boolean
  threadsConnectionId?: string
  setStartLineNumber?: (lineNumber: number | undefined) => void
}

/**
 * The StartThreadComment component is used to render the comment editor when starting a new inline thread.
 */
export function StartThreadComment({
  addCommentDialogTitle,
  isLeftSide,
  fileLevelComment,
  filePath,
  focusOnMount = true,
  lineNumber,
  onAddComment,
  onClose,
  viewerData,
  threadsConnectionId,
  showOnCloseIcon = true,
  ...rest
}: StartThreadCommentProps) {
  const [commentLineChangeConfig, setCommentLineChangeConfig] = useState<CommentLineChangeConfig | undefined>(undefined)
  const moveNewCommentEnabled = useFeatureFlag('move_new_comments_ui')
  const handleAddComment = ({
    commentText,
    onCompleted,
    onError,
    submitBatch,
  }: {
    commentText: string
    onCompleted?: (threadId: string, commentDatabaseId?: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean
  }) => {
    const handleCompleted = (threadId: string, commentDatabaseId: number | undefined | null) => {
      if (commentDatabaseId) anchorComment(commentDatabaseId.toString())
      onCompleted?.(threadId, commentDatabaseId)
    }

    onAddComment({
      text: commentText,
      onError,
      onCompleted: handleCompleted,
      submitBatch,
    })
  }

  return (
    <div className="px-2 pb-2 pt-1">
      <div className="d-flex flex-justify-between flex-items-center">
        <Heading as="h4" className="f5 ml-1 pt-1 pb-2 d-flex">
          <GitHubAvatar alt={viewerData.login} size={24} src={viewerData.avatarUrl || ''} className="mr-2" />

          {!moveNewCommentEnabled &&
            (fileLevelComment ? (
              <>Add comment on file</>
            ) : (
              <span>{addCommentDialogTitle ? `Add a comment on ${addCommentDialogTitle}` : 'Add a comment'}</span>
            ))}
          {moveNewCommentEnabled && (
            <>
              <div className="pt-1">Add {fileLevelComment ? '' : 'a'} comment on </div>
              <ChangeCommentLineNumberButton
                currentLineNumberText={fileLevelComment ? 'file' : addCommentDialogTitle ?? ''}
                baseEndLineNumber={lineNumber}
                baseStartLineNumber={rest.startLineNumber}
                diffSide={isLeftSide ? 'LEFT' : 'RIGHT'}
                setCommentLineChangeConfig={setCommentLineChangeConfig}
              />
            </>
          )}
        </Heading>
        {onClose && showOnCloseIcon && (
          <IconButton variant="invisible" icon={XIcon} onClick={() => onClose()} aria-label="Cancel" />
        )}
      </div>
      <AddCommentEditor
        focusOnMount={focusOnMount}
        condensed={false}
        fileLevelComment={fileLevelComment ?? false}
        filePath={filePath}
        lineNumber={lineNumber}
        onAddComment={handleAddComment}
        onCancelComment={onClose}
        commentLineChangeConfig={commentLineChangeConfig}
        side={isLeftSide ? 'LEFT' : 'RIGHT'}
        {...rest}
      />
    </div>
  )
}

try{ StartThreadComment.displayName ||= 'StartThreadComment' } catch {}